/* eslint-disable @typescript-eslint/no-explicit-any */
import { ColDef, GetRowIdParams, GridReadyEvent, RowSelectedEvent } from '@ag-grid-community/core';
import { Component, ViewChild, ViewEncapsulation } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import {
  AgFns,
  GridOptionsExt,
  ISortModel,
  ProxAgFns,
  RowSelectionMultipleCheckbox,
  SaveAndQueryComponent,
  StatusService,
  wrapBusy,
} from '@core';
import { BaseService } from '@core';
import { DateFns, UtilFns } from '@utils';
import * as _ from 'lodash-es';
import { EntityFns } from '@data';
import { EntityError } from 'breeze-client';
import {
  ProgramUserGroup,
  ActiveStatus,
  ActiveStatusEnum,
  ProgramUserGroupBudget,
  Account,
  ProgramUserGroupMap,
  PurchaseOrder,
  AccountProcurementCard,
  AccountAddress,
  ProgramUserGroupExclusion,
  ProximityUserNote,
  NotificationEventEnum,
  JobOrderBudgetLog,
} from '@models';
import { AccountDbSaveService } from '../services/account-db-save.service';
import { AccountDbQueryService, PugAccountUserCount } from '../services/account-db-query.service';
import { AccountUserFinderDialog } from './account-user-finder.dialog';
import { DialogService } from 'primeng/dynamicdialog';
import { BudgetItemsListComponent } from '../budgets/budget-items-list.component';
import { AccountActivationService } from '../services/account-activation.service';
import { PugCurrentProgramsDialogComponent, PugCurrentProgramsDialogParams } from './pug-current-programs-dialog.component';
import { MenuItem } from 'primeng/api';
import { AnalysisGridDialogComponent, AnalysisGridDialogParams } from '@core';

@Component({
  selector: 'prox-program-user-groups',
  templateUrl: './program-user-groups.component.html',
  standalone: false,
  encapsulation: ViewEncapsulation.None,
  styles: [
    `
      prox-program-user-groups {
        .p-tabpanels {
          padding: 0 !important;
        }
      }
    `,
  ],
})
export class ProgramUserGroupsComponent extends SaveAndQueryComponent {
  @ViewChild(BudgetItemsListComponent)
  budgetListItemsComponent!: BudgetItemsListComponent;
  supplierId!: string;
  accountId!: string;
  account!: Account;
  tabViewIndex = 0;
  activeStatuses!: ActiveStatus[];

  allPugs!: ProgramUserGroup[];
  pugGridOptions!: GridOptionsExt;
  pugs: ProgramUserGroup[] = [];
  selectedPug?: ProgramUserGroup;
  showInactivePugs = false;
  /** Per-PUG "current program" count (Active + not-ended) — drives the read-only
   *  Current Programs column on the PUG grid. */
  currentProgramCountMap = new Map<string, number>();

  /** The "Analyze" dropdown above the PUG grid. Every option opens the shared
   *  AnalysisGridDialogComponent, which carries the full ag-Grid Enterprise tool set. */
  analyzeItems: MenuItem[] = [
    {
      label: 'Account Users per Program User Group',
      icon: 'pi pi-users',
      command: () => this.onAnalyzeAccountUsersPerPug(),
    },
  ];

  pugmGridOptions!: GridOptionsExt;
  pugms: ProgramUserGroupMap[] = [];
  billingAddresses: AccountAddress[] = [];

  programUserGroupBudgets: ProgramUserGroupBudget[] = [];
  joBudgetLogs: JobOrderBudgetLog[] = [];
  purchaseOrders: PurchaseOrder[] = [];
  pcards: AccountProcurementCard[] = [];
  languages = ['English', 'Spanish', 'French', 'Portuguese', '(other)'];
  isValidateActivated = false;

  constructor(
    baseService: BaseService,
    route: ActivatedRoute,
    override dbSaveService: AccountDbSaveService,
    override dbQueryService: AccountDbQueryService,
    public pngDialogService: DialogService,
    public activationService: AccountActivationService,
    private statusService: StatusService,
  ) {
    super(baseService, route, dbSaveService, dbQueryService);
  }

  override async updateFromParams(params: object): Promise<void> {
    this.accountId = params['accountId'];
    UtilFns.assertNonEmptyString(this.accountId, 'accountId');

    const [account, activeStatuses, pos, pcards, billingAddresses, r4, currentCounts] = await Promise.all([
      this.dbQueryService.getAccountById(this.accountId),
      this.dbQueryService.getAll(ActiveStatus),
      this.dbQueryService.getAccountPurchaseOrders(this.accountId),
      this.dbQueryService.getAccountProcurementCards(this.accountId),
      this.dbQueryService.getBillingAccountAddresses(this.accountId),
      this.dbQueryService.getProgramUserGroupsWithBudgets(this.accountId),
      this.dbQueryService.getCurrentProgramCountsForPugs(this.accountId),
    ]);
    this.currentProgramCountMap = new Map(currentCounts.map((c) => [c.ProgramUserGroupId, c.CurrentProgramCount]));
    this.account = account;
    UtilFns.assertNonNull(this.account, 'Account');
    this.activeStatuses = activeStatuses;
    this.billingAddresses = billingAddresses;

    this.purchaseOrders = pos
      .filter((p) => !p.isParent())
      .map((p) => {
        return new PurchaseOrder(p.id, p.nameAndStatus);
      });
    this.purchaseOrders.unshift({ id: null, purchaseOrder: '- None -' } as PurchaseOrder);

    this.pcards = pcards;
    this.pcards.unshift({ id: null, namedesc: '- None -' } as any as AccountProcurementCard);

    this.pugGridOptions = AgFns.initGridOptions(this, {
      onGridReady: this.onPugGridReady,
      onRowSelected: this.onPugRowSelected,
      rowModelType: 'clientSide',
      getRowId: (rowIdParams: GetRowIdParams) => {
        const pug = rowIdParams.data as ProgramUserGroup;
        return pug.id;
      },
    });

    this.isValidateActivated = this.activationService.hasErrors;
    if (this.isValidateActivated) {
      this.activationService.activate(this.account.id);
    }

    AgFns.captureGridRouteParams(this.pugGridOptions, this.route, 'id');

    this.pugmGridOptions = AgFns.initGridOptions(this, {
      onGridReady: this.onPugmGridReady,
      rowModelType: 'clientSide',
      getRowId: (rowIdParams: GetRowIdParams) => {
        const pugm = rowIdParams.data as ProgramUserGroupMap;
        return pugm.accountUserId;
      },
    });

    //await this.evaluateValidation();

    this.allPugs = r4;
    this.setTitle('Program User Groups for ' + this.account.name);
    this.isPageReady = true;
  }

  isReadOnly() {
    return this.statusService.getWorkingStatus(this.account).isActiveReadOnly;
  }

  canDelete() {
    return !this.isReadOnly();
  }

  statusMessage() {
    let msg = '';
    if (this.account?.activeStatusId == ActiveStatusEnum.Active) {
      return 'Account is Active and Not Editable.  To edit, set Account status to Hold.';
    } else if (this.account?.activeStatusId == ActiveStatusEnum.Hold) {
      msg = 'Account is On-Hold and is Editable';
    } else {
      return 'Account is ' + this.account?.activeStatus.name + ' and is Not Editable';
    }
    return msg;
  }

  // Aug Grid

  async onPugGridReady(event: GridReadyEvent) {
    const [colDefs, sortModel] = this.getPugColDefsAndSortModel();

    AgFns.initGrid(event, colDefs, sortModel);
    await this.refreshPugGrid();
    AgFns.applyGridRouteParams(this.pugGridOptions);
  }

  getPugColDefsAndSortModel() {
    const colDefs: ColDef[] = [
      ProxAgFns.getEntityStateColDef(),
      {
        headerName: 'Program User Group Name',
        field: 'name',
        editable: (p) => !this.isReadOnly() && !(p.data as ProgramUserGroup)?.consolidatedIntoPugId,
        filter: 'agTextColumnFilter',
        // Consolidated losers get a suffix tag + tooltip so admins recognize them at a glance.
        // The status column is gated separately (isEditable) — this is purely visual.
        cellRenderer: (p: any) => {
          const pug = p.data as ProgramUserGroup;
          if (pug?.consolidatedIntoPugId) {
            return `${pug.name} <span class="text-xs italic text-gray-500" title="Consolidated into another PUG on ${pug.consolidatedOn ?? ''}. Only Global Administrators may reactivate.">(consolidated)</span>`;
          }
          return pug?.name ?? '';
        },
        ...AgFns.flexWidth(200),
      },
      {
        ...ProxAgFns.getActiveStatusColDef('activeStatus.name'),
        ...AgFns.fixWidth(140),
        sortable: false,
      },
      {
        headerName: 'Current Programs',
        colId: 'currentProgramCount',
        valueGetter: (p) => this.currentProgramCountMap.get((p.data as ProgramUserGroup)?.id) ?? 0,
        type: 'numericColumn',
        cellClass: ['cell-numeric'],
        cellStyle: (p) => (p.value > 0 ? { color: 'blue', cursor: 'pointer', textDecoration: 'underline' } : null),
        onCellClicked: (event) => {
          if ((event.value ?? 0) > 0) {
            this.showCurrentProgramsDialog(event.data as ProgramUserGroup);
          }
        },
        sortable: true,
        ...AgFns.fixWidth(140),
      },
      ProxAgFns.getEntityStatusColDef(this.onChangeActiveStatus.bind(this), { canDisplay: this.canChangeStatus.bind(this) }),
      ProxAgFns.getEntityDeleteColDef(this.onPugDelete.bind(this), { canDisplay: this.canDelete.bind(this) }),
    ];
    const sortModel: ISortModel = [{ colId: 'name', sort: 'asc' }];
    return [colDefs, sortModel] as const;
  }

  isEditable() {
    return this.account.activeStatusId === ActiveStatusEnum.Hold;
  }

  /** Status column visibility gate. In addition to the account-Hold rule, a PUG that was
   *  consolidated into another via the PUG Consolidation utility is locked to Global-Admin-
   *  only reactivation — the server also enforces this in BeforeSaveHandler. */
  canChangeStatus(pug: ProgramUserGroup): boolean {
    if (!this.isEditable()) return false;
    if (pug?.consolidatedIntoPugId && !this.authUser?.isGlobalAdmin) return false;
    return true;
  }

  async onChangeActiveStatus(pug: ProgramUserGroup) {
    const valFn = async (selectedStatusId: ActiveStatusEnum, errs: string[]) => {
      if (selectedStatusId === ActiveStatusEnum.Active || selectedStatusId === ActiveStatusEnum.Phaseout) {
        const hasActiveUser = pug.programUserGroupMaps.some((m) => m.accountUser.proximityUser?.activeStatusId === ActiveStatusEnum.Active);
        if (!hasActiveUser) {
          errs.push(`The Program User Group, '${pug.name}', must have at least one Active Account User to be activated or phased-out.`);
        }
      }
      return true;
    };
    await this.statusService.onChangeActiveStatus(this, pug, 'Program User Group', valFn);
    this.pugGridOptions.apiX?.refreshCells();
  }

  async onPugRowSelected(event: RowSelectedEvent) {
    if (!event.node.isSelected()) return;
    const pug = event.data as ProgramUserGroup;
    if (pug == null) return;

    // Guard: on accounts that have consolidated PUGs, the ProgramUserGroup self-nav
    // (ConsolidatedIntoPug) confuses Breeze's ref-resolver during list materialization,
    // leaving some cached entities with entityAspect === undefined. That + Add-then-Undo
    // (which detaches the Added entity, also nulling entityAspect) both surface here.
    // Same root cause as the finder-dialog blank-rows issue that led us to bypass Breeze
    // in the picker.
    if (pug.entityAspect == null) return;

    if (pug.entityAspect.entityState.isDeleted()) {
      this.selectedPug = undefined;
      this.pugGridOptions.apiX?.deselectAll();
      return;
    }

    this.updateGridRouteParams(this.pugGridOptions, pug.id);

    await this.dbQueryService.getProgramUserGroupMaps(pug.id);
    // don't set this until pug details have been queried.
    this.selectedPug = pug;
    // we want to pick up all added (but not yet saved) records here.
    // A freshly-created (Added, not-yet-saved) PUG has undefined nav collections; coerce to []
    // so the child budget/user grids don't NRE on .map (surfaced by Add-then-Undo — Undo's
    // explicit refreshBudgets() call makes the latent undefined loud).
    this.pugms = pug.programUserGroupMaps ?? [];
    AgFns.refreshGrid(this.pugmGridOptions, this.pugms);
    this.programUserGroupBudgets = pug.programUserGroupBudgets ?? [];

    this.joBudgetLogs = await this.dbQueryService.getJobOrderBudgetLogsForProgramUserGroup(
      pug.id,
      this.account.getCurrentYearFiscalStartDate(),
    );

    this.budgetListItemsComponent.refreshGrid();
  }

  showCurrentProgramsDialog(pug: ProgramUserGroup) {
    if (pug == null) return;
    this.pngDialogService.open(PugCurrentProgramsDialogComponent, {
      header: `Current Programs — ${pug.name}`,
      width: '800px',
      height: '500px',
      contentStyle: { overflow: 'hidden' },
      closable: true,
      modal: true,
      resizable: true,
      data: {
        programUserGroupId: pug.id,
      } as PugCurrentProgramsDialogParams,
    });
  }

  /** Analyze > Account Users per Program User Group.
   *  Counts the live Account Users on each live PUG, and names the current Programs the group
   *  serves. "Live" means not Inactive, so Hold and Phaseout rows are included. Users with a
   *  past ExitDate are excluded. The Programs match the grid's own Current Programs column. */
  @wrapBusy
  async onAnalyzeAccountUsersPerPug() {
    const rows = await this.dbQueryService.getAccountUserCountsPerPug(this.accountId);
    const colDefs: ColDef[] = [
      {
        field: 'ProgramUserGroupName',
        headerName: 'Program User Group',
        minWidth: 280,
        enableRowGroup: true,
        filter: 'agTextColumnFilter',
      },
      {
        field: 'ActiveStatusName',
        headerName: 'Status',
        maxWidth: 120,
        enableRowGroup: true,
        filter: 'agSetColumnFilter',
      },
      {
        field: 'AccountUserCount',
        headerName: 'Account Users',
        maxWidth: 150,
        type: 'numericColumn',
        aggFunc: 'sum',
        filter: 'agNumberColumnFilter',
      },
      {
        field: 'ProgramCount',
        headerName: 'Programs',
        maxWidth: 130,
        type: 'numericColumn',
        aggFunc: 'sum',
        filter: 'agNumberColumnFilter',
      },
      {
        field: 'Programs',
        headerName: 'Program Names',
        minWidth: 320,
        tooltipField: 'Programs',
        filter: 'agTextColumnFilter',
      },
    ];

    const totalUsers = (rows as PugAccountUserCount[]).reduce((sum, r) => sum + r.AccountUserCount, 0);

    this.pngDialogService.open(AnalysisGridDialogComponent, {
      header: `Account Users per Program User Group — ${this.account.name}`,
      width: '85vw',
      height: '75vh',
      contentStyle: { overflow: 'hidden' },
      closable: true,
      modal: true,
      resizable: true,
      maximizable: true,
      data: {
        title: 'Account Users per Program User Group',
        subtitle:
          `${rows.length} active group(s), ${totalUsers} membership(s). ` +
          'Inactive groups, inactive users and users past their exit date are left out. ' +
          'Programs are the current ones — not Inactive, and not past their end date.',
        rowData: rows,
        columnDefs: colDefs,
        exportFileName: 'account-users-per-pug',
      } as AnalysisGridDialogParams,
    });
  }

  async onPugAdd() {
    const newPug = this.dbSaveService.createEntity(ProgramUserGroup, {
      accountId: this.accountId,
      activeStatusId: ActiveStatusEnum.Hold,
    });
    newPug.entityAspect.validateEntity();
    this.allPugs.push(newPug);
    this.refreshPugGrid();
    const gridApi = this.pugGridOptions.apiX;
    if (!gridApi) return;

    await AgFns.selectGridRowByKey(this.pugGridOptions, (e) => e.id, newPug.id, 'name');
  }

  async onPugDelete(pug: ProgramUserGroup) {
    // just in case
    await this.dbQueryService.getProgramUserGroupMaps(pug.id);
    if (pug.programUserGroupMaps.length > 0) {
      await this.dialogService.okDialog(
        'Cannot delete Program User Group',
        `<b>${pug.name}</b> still has users assigned.<br/><br/>` +
          `Remove all users from this Program User Group first, then try again.`,
      );
      return;
    }

    // Server-side pre-delete check covers every FK-holding table (Programs via ApprovalTree,
    // Issuances, Allowances, Kits, Billing Configs, Budgets, Job Orders, consolidation losers).
    // Client-side checkIfInUse only saw ATUGs and missed the other blockers, which then
    // surfaced as a raw SQL FK error on save.
    const blockers = await this.dbQueryService.getPugDeleteBlockers(pug.id);
    if (blockers.length > 0) {
      const body = blockers
        .map((b) => {
          const sample = b.SampleNames.length > 0 ? `<br/>&nbsp;&nbsp;&bull; ${b.SampleNames.join('<br/>&nbsp;&nbsp;&bull; ')}` : '';
          const more = b.Count > b.SampleNames.length && b.SampleNames.length > 0
            ? `<br/>&nbsp;&nbsp;<i>&hellip; and ${b.Count - b.SampleNames.length} more</i>`
            : '';
          return `<b>${b.Category} (${b.Count})</b>${sample}${more}`;
        })
        .join('<br/><br/>');
      await this.dialogService.okDialog(
        'Cannot delete Program User Group',
        `<b>${pug.name}</b> is still referenced by the following:<br/><br/>${body}<br/><br/>` +
          `Remove those references first, or set the Program User Group to Inactive instead.`,
      );
      return;
    }

    pug.programUserGroupMaps.slice().forEach((x) => EntityFns.deleteOrDetach(x.entityAspect));
    pug.programUserGroupExclusions.slice().forEach((x) => EntityFns.deleteOrDetach(x.entityAspect));
    EntityFns.deleteOrDetach(pug.entityAspect);
    this.selectedPug = undefined;
    this.pugms = [];
    if (this.allPugs) {
      _.remove(this.allPugs, pug);
      this.refreshPugGrid();
    }
  }

  onShowInactiveChanged() {
    this.refreshPugGrid();
  }

  async refreshPugGrid(fullRefresh = false) {
    // Filter out Inactive PUGs unless the user has checked "Show Inactive?" above the grid.
    // In-memory filter — allPugs is already loaded; we don't need to re-query.
    this.pugs = this.showInactivePugs
      ? this.allPugs.slice()
      : this.allPugs.filter((p) => p.activeStatusId !== ActiveStatusEnum.Inactive);
    if (this.selectedPug) {
      this.selectedPug = this.pugs.includes(this.selectedPug) ? this.selectedPug : undefined;
    }
    await AgFns.refreshGrid(this.pugGridOptions, this.pugs || []);
    if (fullRefresh) {
      if (this.selectedPug) {
        AgFns.selectGridRowByKey(this.pugGridOptions, (e) => e.id, this.selectedPug.id);
      } else if (this.pugs.length > 0) {
        this.selectedPug = await AgFns.selectFirstRow(this.pugGridOptions);
      }
    }

    if (this.selectedPug == null) {
      this.pugms = [];
      this.programUserGroupBudgets = [];
    } else {
      this.pugms = this.selectedPug.programUserGroupMaps;
      this.programUserGroupBudgets = this.selectedPug.programUserGroupBudgets;
    }
    AgFns.refreshGrid(this.pugmGridOptions, this.pugms);

    this.budgetListItemsComponent.refreshGrid();
  }

  // User Grid

  onPugmGridReady(event: GridReadyEvent) {
    const [colDefs, sortModel] = this.getPugmColDefsAndSortModel();

    AgFns.initGrid(event, colDefs, sortModel);
  }

  getPugmColDefsAndSortModel() {
    const colDefs: ColDef[] = [
      {
        ...AgFns.createButtonProps('', this.onPugmEdit.bind(this), {
          label: 'Edit',
          calcDisabled: () => this.dbSaveService.hasChanges(),
        }),
        ...AgFns.fixWidth(50),
        suppressHeaderMenuButton: true,
      },
      ProxAgFns.getEntityStateColDef(),
      {
        headerName: 'Last Name',
        field: 'accountUser.proximityUser.lastName',
        filter: 'agTextColumnFilter',
        ...AgFns.flexWidth(140),
      },
      {
        headerName: 'First Name',
        field: 'accountUser.proximityUser.firstName',
        filter: 'agTextColumnFilter',
        ...AgFns.flexWidth(140),
      },
      {
        headerName: 'M.I.',
        field: 'accountUser.proximityUser.middleInitial',
        ...AgFns.fixWidth(100),
      },
      {
        headerName: 'Join Date',
        field: 'joinDate',
        filter: 'agDateColumnFilter',
        cellEditor: 'agDateCellEditor',
        editable: true,
        ...AgFns.fixWidth(160),
      },
      {
        headerName: 'Email Address',
        field: 'accountUser.proximityUser.email',
        filter: 'agTextColumnFilter',
        ...AgFns.flexWidth(200),
      },
      { ...ProxAgFns.getActiveStatusColDef('accountUser.proximityUser.activeStatus.name'), ...AgFns.fixWidth(100) },
      ProxAgFns.getEntityDeleteColDef(this.onPugmDelete.bind(this), { canDisplay: this.canDelete.bind(this) }),
    ];

    const sortModel: ISortModel = [
      { colId: 'accountUser.proximityUser.lastName', sort: 'asc' },
      { colId: 'accountUser.proximityUser.firstName', sort: 'asc' },
    ];
    return [colDefs, sortModel] as const;
  }

  /**
   * An Inactive PUG routes no orders, so adding users to it grants nothing while looking like
   * access — the same trap the Account User form's attach picker now closes. The "Show Inactive?"
   * checkbox puts Inactive PUGs back in this grid, where they are selectable, so the button has
   * to gate on status rather than on selection alone. The server refuses the save either way
   * (BeforeSaveHandler.CheckProgramUserGroupMapAdds); this is so the admin does not get there.
   *
   * Hold and Phaseout stay attachable on purpose. Hold is by design a temporary state, so the
   * membership is early rather than dead; Phaseout is a valid state for a PUG. Inactive is the
   * only status that means the group is finished.
   */
  canAttachUsers(): boolean {
    if (!this.selectedPug || this.isReadOnly()) return false;
    return this.selectedPug.activeStatusId !== ActiveStatusEnum.Inactive;
  }

  attachUsersDisabledReason(): string {
    if (!this.selectedPug || this.isReadOnly()) return '';
    if (this.selectedPug.activeStatusId !== ActiveStatusEnum.Inactive) return '';
    return this.selectedPug.consolidatedIntoPugId
      ? 'This group was consolidated into another group. Add the users to the group it was merged into.'
      : 'This group is Inactive, so a membership would grant no access. Reactivate it first.';
  }

  @wrapBusy
  async onPugmAdd() {
    const newPugms = await this.attachUsers();
    if (newPugms.length == 0) return;
    this.pugms.push(...newPugms);
    AgFns.refreshGrid(this.pugmGridOptions, this.pugms);
    AgFns.updateMasterDetail(this.pugGridOptions, this.selectedPug);
  }

  async attachUsers() {
    if (this.selectedPug == null) return [];
    const pug = this.selectedPug;

    const allAccountUsers = await this.dbQueryService.getAccountUsers(this.accountId);

    const currentAccountUserIds = new Set(this.selectedPug.programUserGroupMaps.map((y) => y.accountUserId));
    const remainingAccountUsers = allAccountUsers.filter((au) => !currentAccountUserIds.has(au.id));
    const newAccountUsers = await this.dialogService.createFinder(AccountUserFinderDialog, {
      accountUsers: remainingAccountUsers,
      rowSelection: RowSelectionMultipleCheckbox,
    });
    if (newAccountUsers.length == 0) return [];
    const newPugms = newAccountUsers.map((accountUser) => {
      let pugm = this.dbSaveService.uow.undoIfDeleted(ProgramUserGroupMap, [pug.id, accountUser.id]);
      if (pugm == null) {
        pugm = this.dbSaveService.createEntity(ProgramUserGroupMap, {
          programUserGroupId: pug.id,
          accountUserId: accountUser.id,
          joinDate: new Date(),
        });
        this.dbSaveService.createEntity(ProximityUserNote, {
          proximityUserId: accountUser.proximityUserId,
          note: `Added to Program User Group: ${pug.name}`,
          isSystemGenerated: true,
          noteTs: new Date(),
        });
      }
      return pugm;
    });
    const wereAdded = await this.createExclusionsForDupUsers(pug, newPugms);

    return newPugms;
  }

  async createExclusionsForDupUsers(pug: ProgramUserGroup, addedPugms: ProgramUserGroupMap[]) {
    // includeUserGroupMaps: this is the one caller that walks OTHER users' memberships - it
    // counts how many times each person reaches a program through the tree - so it needs the
    // rows that every other caller deliberately leaves behind.
    let programs = await this.dbQueryService.getProgramsForProgramUserGroups([pug.id], false, true);
    // We only have to fix up active or phaseout programs.
    programs = programs.filter((x) => x.activeStatusId === ActiveStatusEnum.Active || x.activeStatusId === ActiveStatusEnum.Phaseout);
    if (programs.length === 0) return false;

    const pugExs = pug.programUserGroupExclusions; // fetched by the previous query.
    const counts = programs.map((program) => {
      let programPugms = _.flatten(program.getProgramUserGroups().map((x) => x.programUserGroupMaps));
      programPugms = [...programPugms, ...addedPugms];
      const exclusions = pug.programUserGroupExclusions.filter((x) => x.programId === program.id);
      programPugms = programPugms.filter((x) => !exclusions.some((y) => y.accountUserId === x.accountUserId));
      const ausIds = programPugms.map((x) => x.accountUserId);
      const grouped = _.groupBy(ausIds);
      const duplicateGroups = _.filter(grouped, (group) => group.length > 1);
      const dupAusIds = _.uniq(_.flatten(duplicateGroups));
      for (const dupAusId of dupAusIds) {
        const pugEx = this.dbSaveService.createEntity(ProgramUserGroupExclusion, {
          programId: program.id,
          programUserGroupId: pug.id,
          accountUserId: dupAusId,
        });
      }
      return dupAusIds.length > 0;
    });
    // check if any exclusions were created.
    return counts.some((x) => x);
  }

  onPugmEdit(pugm: ProgramUserGroupMap, _params?: any, event?: UIEvent) {
    const accountUserId = pugm.accountUserId;
    const navCommands = ['account/accounts', this.accountId, 'manage', 'account-users', accountUserId];
    const mouseEvent = event as MouseEvent | undefined;
    if (mouseEvent?.ctrlKey || mouseEvent?.metaKey) {
      // App uses HashLocationStrategy — router.serializeUrl returns the path only,
      // so prepend '#' or the new tab lands at root and gets redirected home.
      const url = '#' + this.router.serializeUrl(this.router.createUrlTree(navCommands));
      window.open(url, '_blank');
    } else {
      this.router.navigate(navCommands);
    }
  }

  @wrapBusy
  async onPugmDelete(pugm: ProgramUserGroupMap) {
    if (!this.selectedPug) return;

    if (this.dbSaveService.hasChanges()) {
      await this.dialogService.okDialog('Cannot Remove User', 'Please save all changes before removing a user from a user group.');
      return;
    }

    // Same pre-flight as the Account User page — see the long note on its onPugmDelete.
    // Any in-flight order under this membership blocks removal; name them rather than
    // offering a removal the server will reject.
    const blockingOrders = await this.dbQueryService.getUnplacedJobOrdersForUserAndProgramUserGroup(
      pugm.accountUserId,
      pugm.programUserGroupId,
    );
    if (blockingOrders.length > 0) {
      const orderList = blockingOrders.map((jo) => `<li>${jo.displayId} — ${jo.friendlyStatusName()}</li>`).join('');
      await this.dialogService.okDialog(
        'Cannot Remove User',
        `${pugm.accountUser.proximityUser.fullName} cannot be removed from '${pugm.programUserGroup.name}'
        while they have open orders under it.
        <br/><br/>
        <ul>${orderList}</ul>
        Cancel or finish placing ${blockingOrders.length === 1 ? 'this order' : 'these orders'}, then remove the membership.`,
      );
      return;
    }

    const ok = await this.dialogService.askIfOk(
      'Remove user from user group',
      `Are you sure you want to remove this user from the user group?`,
    );
    if (!ok) return;

    // TODO: ??? why is this next line necessary
    this.addEntitiesToValidate(pugm);

    // need to capture proximity user before deleting the pugm
    const proximityUser = pugm.accountUser.proximityUser;

    this.dbSaveService.createEntity(ProximityUserNote, {
      proximityUserId: pugm.accountUser.proximityUserId,
      note: `Removed from Program User Group: ${pugm.programUserGroup.name}`,
      isSystemGenerated: true,
      noteTs: new Date(),
    });
    EntityFns.deleteOrDetach(pugm.entityAspect);

    // No job-order cascade here — the pre-flight above guarantees there are none to delete.
    // (The cascade this replaced was doubly broken: the server guard rejected it, and it was
    // written as `forEach(async ...)`, which never awaited the deletions anyway.)

    const pugExsToDelete = await this.dbQueryService.getProgramUserGroupExclusionsForProgramUserGroupMap(pugm);
    pugExsToDelete.forEach((pugEx) => EntityFns.deleteOrDetach(pugEx.entityAspect));
    _.remove(this.pugms, pugm);

    const notifCount = await this.dbSaveService.createNotifications({
      NotificationEventId: NotificationEventEnum.FreeForm,
      FreeFormTemplate: `User: ${proximityUser.fullName} has been removed from Program User Group, '${this.selectedPug.name}'`,
      QueryMap: {},
      AccountId: this.account.id,
      ProximityUserId: proximityUser.id,
      Priority: 1,
    });

    await this.dbSaveService.saveChanges();
    this.toastr.success(`User removed from Program User Group.`, 'User Removed');
    if (notifCount > 0) {
      this.toastr.info(`${notifCount} notification(s) queued`, 'Notifications');
    }
    await AgFns.refreshGrid(this.pugmGridOptions, this.pugms);
  }

  // UgBudget grid

  get pugBudgetGridOptions() {
    return this.budgetListItemsComponent?.budgetGridOptions;
  }

  createPugBudget() {
    const ugb = this.dbSaveService.createEntity(ProgramUserGroupBudget, {
      accountId: this.accountId,
      programUserGroupId: this.selectedPug?.id,
      fiscalStartDate: this.account.getCurrentYearFiscalStartDate(),
    });
    return ugb;
  }

  // -----------------------------------------------------------------------

  onTabChange(event: any) {
    if (event.index == 1) {
      AgFns.sizeColumnsToFit(this.pugBudgetGridOptions);
    }
  }

  override async addCrossValidationErrors() {
    EntityFns.checkForDupErrors(
      this.allPugs,
      (x) => x.name,
      (e, dupName) => this.createValidationError(e, 'name', `This Program User Group name is a duplicate: '${dupName}.`),
    );

    const pugs = this.getEntitiesToValidate().filter((x) => x instanceof ProgramUserGroup) as ProgramUserGroup[];

    // Program User Groups must have at least one Active Account User to be activated
    pugs
      .filter((pug) => pug.activeStatusId === ActiveStatusEnum.Active)
      .forEach((pug) => {
        const hasActiveUser = pug.programUserGroupMaps.some((m) => m.accountUser.proximityUser?.activeStatusId === ActiveStatusEnum.Active);
        if (!hasActiveUser) {
          this.createValidationError(
            pug,
            'name',
            `The Program User Group, '${pug.name}', must have at least one Active Account User to be activated.`,
          );
        }
      });

    this.allPugs.forEach((x) => {
      const budgets = x.programUserGroupBudgets;
      if (!budgets.some((y) => y.entityAspect.entityState.isAdded() || y.entityAspect.entityState.isModified())) {
        return;
      }
      EntityFns.checkForDupErrors(
        budgets,
        (x) => (x.budgetProductTypeTag?.name || '- Any -') + ' - ' + DateFns.fmtDateShort(x.fiscalStartDate),
        (e, dupName) =>
          this.createValidationError(e, 'name', `Duplicate Budget Product Tag Name within the same fiscal year: '${dupName}'`),
      );
    });
  }

  override async afterSave() {
    if (this.isValidateActivated) {
      if (!(await this.activationService.activate(this.account.id))) {
        this.account.activeStatusId = ActiveStatusEnum.Active;
        this.activationService.deactivate();
        this.isValidateActivated = false;
        await this.saveChanges();
        await this.dialogService.okDialog('Activation Status', "Account is successfully updated to 'Active'");
        await this.refreshPugGrid(true);
      }
    }
    return this.afterUndo();
  }

  override async afterUndo() {
    this.allPugs = await this.dbQueryService.getProgramUserGroups(this.accountId);
    this.budgetListItemsComponent.refreshBudgets();
    this.refreshPugGrid(true);

    // AgFns.updateMasterDetail(this.augGridOptions, this.selectedAug);
  }

  override async navigateToValidationError(error: EntityError) {
    const errEnt = error.entity;
    if (errEnt instanceof ProgramUserGroup) {
      AgFns.selectGridRowByKey(this.pugGridOptions, (e: ProgramUserGroup) => e.id, errEnt.id, error.propertyName);
    } else if (errEnt instanceof ProgramUserGroupBudget) {
      this.tabViewIndex = 1;
      AgFns.selectGridRowByKey(this.pugGridOptions, (e: ProgramUserGroup) => e.id, errEnt.programUserGroupId);
      UtilFns.wait(0);
      AgFns.selectGridRowByKey(this.pugBudgetGridOptions, (e: ProgramUserGroupBudget) => e.id, errEnt.id, error.propertyName);
    }
  }
}
